const SESSION_KEY = "pl-session-token-v2";

const getApiBaseUrl = () => {
    if (process.env.REACT_APP_PL_API_BASE) {
        return process.env.REACT_APP_PL_API_BASE;
    }

    const hostname = String(window.location.hostname || "").toLowerCase();
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
        return `${window.location.protocol}//${hostname}:3002/api/pl`;
    }

    return "/api/pl";
};

const API_BASE_URL = getApiBaseUrl();
let visiblePostCount = 10;
const UPLOAD_INACTIVITY_TIMEOUT_MS = 90 * 1000;

const getAssetBaseUrl = () => {
    if (process.env.REACT_APP_PL_ASSET_BASE) {
        return process.env.REACT_APP_PL_ASSET_BASE.replace(/\/$/, "");
    }

    const hostname = String(window.location.hostname || "").toLowerCase();
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
        return `${window.location.protocol}//${hostname}:3002`;
    }

    return "";
};

const ASSET_BASE_URL = getAssetBaseUrl();

const resolveMediaUrl = (mediaUrl) => {
    if (!mediaUrl) {
        return null;
    }

    if (/^https?:\/\//i.test(mediaUrl)) {
        return mediaUrl;
    }

    return ASSET_BASE_URL ? `${ASSET_BASE_URL}${mediaUrl}` : mediaUrl;
};

const normalizePost = (post) => {
    if (!post || typeof post !== "object") {
        return post;
    }

    return {
        ...post,
        mediaUrl: resolveMediaUrl(post.mediaUrl),
        comments: Array.isArray(post.comments)
            ? post.comments.map((comment) => ({
                ...comment,
            }))
            : [],
    };
};

const getSessionToken = () => window.localStorage.getItem(SESSION_KEY) || "";

const setSessionToken = (token) => {
    if (token) {
        window.localStorage.setItem(SESSION_KEY, token);
        return;
    }

    window.localStorage.removeItem(SESSION_KEY);
};

const request = async (path, options = {}) => {
    const token = getSessionToken();
    const headers = new Headers(options.headers || {});

    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    headers.set("X-PL-Post-Limit", String(visiblePostCount));

    const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;

    if (!isFormData && options.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers,
    });

    let payload = null;

    try {
        payload = await response.json();
    } catch (error) {
        payload = null;
    }

    if (!response.ok) {
        throw new Error((payload && payload.error) || "Request failed.");
    }

    return payload;
};

const uploadRequest = ({ path, formData, onUploadProgress }) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let inactivityTimer = null;

        const clearInactivityTimer = () => {
            if (inactivityTimer) {
                window.clearTimeout(inactivityTimer);
                inactivityTimer = null;
            }
        };

        const resetInactivityTimer = () => {
            clearInactivityTimer();
            inactivityTimer = window.setTimeout(() => {
                xhr.abort();
                reject(new Error("Upload timed out after 90 seconds without progress."));
            }, UPLOAD_INACTIVITY_TIMEOUT_MS);
        };

        xhr.open("POST", `${API_BASE_URL}${path}`);
        xhr.timeout = 0;

        const token = getSessionToken();
        if (token) {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }

        xhr.setRequestHeader("X-PL-Post-Limit", String(visiblePostCount));

        xhr.upload.onprogress = (event) => {
            resetInactivityTimer();

            if (!onUploadProgress || !event.lengthComputable) {
                return;
            }

            onUploadProgress(Math.round((event.loaded / event.total) * 100));
        };

        xhr.onloadstart = resetInactivityTimer;

        xhr.onload = () => {
            clearInactivityTimer();
            let payload = null;

            try {
                payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch (error) {
                payload = null;
            }

            if (xhr.status < 200 || xhr.status >= 300) {
                reject(new Error((payload && payload.error) || "Request failed."));
                return;
            }

            resolve(payload);
        };

        xhr.onerror = () => {
            clearInactivityTimer();
            reject(new Error("Upload failed."));
        };
        xhr.onabort = () => {
            clearInactivityTimer();
            reject(new Error("Upload stopped before it finished."));
        };
        xhr.send(formData);
    });

const normalizeState = (payload) => {
    const normalized = {};
    const source = payload && typeof payload === "object" ? payload : null;

    if (!source) {
        return normalized;
    }

    if (Object.prototype.hasOwnProperty.call(source, "user") || Object.prototype.hasOwnProperty.call(source, "currentUser")) {
        normalized.currentUser = source.user || source.currentUser || null;
    }

    if (Object.prototype.hasOwnProperty.call(source, "posts")) {
        normalized.posts = Array.isArray(source.posts) ? source.posts.map(normalizePost) : [];
    }

    if (Object.prototype.hasOwnProperty.call(source, "pagination")) {
        normalized.pagination = source.pagination || null;
    }

    if (Object.prototype.hasOwnProperty.call(source, "accessRequests") || Object.prototype.hasOwnProperty.call(source, "requests")) {
        normalized.accessRequests = source.accessRequests || source.requests || [];
    }

    return normalized;
};

export const setVisiblePlPostCount = (count) => {
    const normalized = Number.parseInt(count, 10);
    visiblePostCount = Number.isInteger(normalized) && normalized > 0 ? normalized : 10;
};

export const bootstrapPlApp = async ({ limit = 10 } = {}) => {
    const payload = await request(`/bootstrap?limit=${encodeURIComponent(limit)}`);
    return normalizeState(payload);
};

export const fetchPlPosts = async ({ offset = 0, limit = 10 } = {}) => {
    const payload = await request(`/posts?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`);
    return normalizeState(payload);
};

export const requestAccess = async ({ name, email, username, note }) =>
    request("/access-request", {
        method: "POST",
        body: JSON.stringify({ name, email, username, note }),
    });

export const loginWithPassword = async ({ identifier, password }) => {
    const payload = await request("/login", {
        method: "POST",
        body: JSON.stringify({ identity: identifier, password }),
    });

    setSessionToken(payload.sessionToken);
    return normalizeState(payload);
};

export const logoutPlUser = async () => {
    try {
        await request("/logout", {
            method: "POST",
        });
    } finally {
        setSessionToken("");
    }

    return {
        currentUser: null,
        posts: [],
        accessRequests: [],
    };
};

export const createPassword = async ({ token, password }) =>
    request("/create-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
    });

export const createPost = async ({ title, caption, file, onUploadProgress }) => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("caption", caption);

    if (file) {
        formData.append("media", file);
    }

    const payload = await uploadRequest({
        path: "/posts",
        formData,
        onUploadProgress,
    });

    return normalizeState(payload);
};

export const saveComment = async ({ postId, commentId, body }) => {
    const payload = await request(
        commentId ? `/posts/${postId}/comments/${commentId}` : `/posts/${postId}/comments`,
        {
            method: commentId ? "PUT" : "POST",
            body: JSON.stringify({ body }),
        }
    );

    return normalizeState(payload);
};

export const deleteComment = async ({ postId, commentId }) => {
    const payload = await request(`/posts/${postId}/comments/${commentId}`, {
        method: "DELETE",
    });

    return normalizeState(payload);
};

export const toggleToastReaction = async ({ postId, type = "cheers" }) => {
    const payload = await request(`/posts/${postId}/reaction`, {
        method: "POST",
        body: JSON.stringify({ type }),
    });

    return normalizeState(payload);
};

export const toggleCommentReaction = async ({ postId, commentId, type = "cheers" }) => {
    const payload = await request(`/posts/${postId}/comments/${commentId}/reaction`, {
        method: "POST",
        body: JSON.stringify({ type }),
    });

    return normalizeState(payload);
};

export const deletePost = async ({ postId }) => {
    const payload = await request(`/posts/${postId}`, {
        method: "DELETE",
    });

    return normalizeState(payload);
};

export const toggleUserSubscription = async ({ userId }) => {
    const payload = await request(`/users/${userId}/subscription`, {
        method: "POST",
    });

    return normalizeState(payload);
};

export const fetchAccessRequests = async () => {
    const payload = await request("/admin/requests");
    return normalizeState(payload);
};

export const decideAccessRequest = async ({ requestId, decision }) => {
    const payload = await request(`/admin/requests/${requestId}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision }),
    });

    return {
        ...normalizeState(payload),
        setupToken: payload.setupToken,
        setupUrl: payload.setupUrl,
    };
};
